import { useCallback, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  addOsmTileLayer,
  createRentahabPinIcon,
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  formatCoordinates,
  hasValidCoordinates,
  PICKER_DEFAULT_ZOOM,
} from './openStreetMapUtils';
import geocodingService from '../../services/geocodingService';
import './location-map-picker.css';

const LocationMapPicker = ({
  latitude,
  longitude,
  onChange,
  height = 320,
  className = '',
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const syncLocation = useCallback((lat, lng, extra = {}) => {
    onChangeRef.current?.({
      latitude: lat,
      longitude: lng,
      ...extra,
    });
  }, []);

  const reverseGeocode = useCallback((lat, lng) => {
    geocodingService
      .reverse(lat, lng)
      .then((location) => {
        if (location) {
          syncLocation(lat, lng, location);
        }
      })
      .catch(() => {});
  }, [syncLocation]);

  const placeMarker = useCallback((map, lat, lng, { pan = true } = {}) => {
    const icon = createRentahabPinIcon();

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      const marker = L.marker([lat, lng], { icon, draggable: true }).addTo(map);
      marker.on('dragend', () => {
        const { lat: newLat, lng: newLng } = marker.getLatLng();
        syncLocation(newLat, newLng);
        reverseGeocode(newLat, newLng);
      });
      markerRef.current = marker;
    }

    if (pan) {
      map.setView([lat, lng], Math.max(map.getZoom(), PICKER_DEFAULT_ZOOM));
    }
  }, [reverseGeocode, syncLocation]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_MAP_CENTER,
      zoom: DEFAULT_MAP_ZOOM,
      scrollWheelZoom: true,
    });

    addOsmTileLayer(map);

    L.control
      .attribution({ position: 'bottomright', prefix: false })
      .addAttribution('© OpenStreetMap')
      .addTo(map);

    map.on('click', (event) => {
      const { lat, lng } = event.latlng;
      placeMarker(map, lat, lng, { pan: false });
      syncLocation(lat, lng);
      reverseGeocode(lat, lng);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [placeMarker, reverseGeocode, syncLocation]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (hasValidCoordinates(latitude, longitude)) {
      placeMarker(map, Number(latitude), Number(longitude), { pan: true });
    } else if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  }, [latitude, longitude, placeMarker]);

  return (
    <div className={`location-map-picker ${className}`}>
      <div className="location-map-picker__toolbar">
        <p className="location-map-picker__hint">
          Haz clic en el mapa para marcar la ubicación del edificio. También puedes arrastrar el pin.
        </p>
        <span className="location-map-picker__coords">
          {hasValidCoordinates(latitude, longitude)
            ? formatCoordinates(latitude, longitude)
            : 'Coordenadas pendientes'}
        </span>
      </div>
      <div
        ref={mapContainerRef}
        className="location-map-picker__map osm-map-tiles"
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
      />
    </div>
  );
};

export default LocationMapPicker;
